import type { UsageHourlyUnitQuantity, UsageHourlyUnitsResponse } from '@nadoutong/contracts';
import { Link } from 'react-router-dom';
import { useBalance, useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useNxtpathPublicStatus } from '../api/useStatus.ts';
import {
  useUsageByKey,
  useUsageHourly,
  useUsageHourlyUnits,
  useUsageSummary,
} from '../api/useUsage.ts';
import { apiKeyDisplayName } from '../components/apiKeyName.ts';
import { CreditHeroLine } from '../components/CreditStatus.tsx';
import {
  formatTimestamp,
  formatTokens,
  formatUsdMicrosOrCentsAsCnyDisplay,
  formatUsdMicrosOrCentsAsCnyMetered,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { GiftBalanceLine } from '../components/GiftBalanceLine.tsx';
import { PanelError } from '../components/PanelError.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { canAccessApiKeys, hasPermission, useSession } from '../hooks/useSession.tsx';
import type { I18nKey } from '../i18n/index.ts';
import { useT } from '../i18n/index.ts';
import { Chip, KpiCard, PageHeader, Panel, Table, TBody, Td, THead, Th, Tr } from '../ui/index.ts';
import { QuickActions } from './dashboard/QuickActions.tsx';
import { StatusStrip } from './dashboard/StatusStrip.tsx';
import { TREND_QUERY_DAYS, TrendRow } from './dashboard/TrendRow.tsx';
import { formatUnitQuantities } from './usage/format.ts';

/**
 * Tenant dashboard — first surface after sign-in.
 *
 * Layout (Field Notes, epic #950): serif page header, four `KpiCard`
 * stats (balance, this-month cost, active keys count, last request
 * timestamp) + recent-activity and top-keys `Panel`s over responsive
 * `Table`s. The status strip, quick actions and 7-day trend row live
 * in `./dashboard/` (`StatusStrip.tsx`, `QuickActions.tsx`,
 * `TrendRow.tsx`); this file composes them and owns the data reads.
 *
 * We deliberately read multiple endpoints in parallel — React Query
 * handles the concurrency + loading state individually. A stat card
 * stays on `—` until its own query resolves; one slow endpoint does
 * not block the rest of the dashboard.
 *
 * The "last request" value is derived from the most-recent non-empty
 * hourly bucket. Platform-api does not yet surface a direct "last
 * request" timestamp, and adding one would duplicate usage accounting
 * logic; the derived value is good enough for a glance-pace read.
 */

function firstOfMonthIso(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return start.toISOString();
}

function nextMonthStartIso(): string {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
  return start.toISOString();
}

const HINT_LINK_CLASS = 'font-medium text-forest hover:text-forest-bright';

export function Dashboard(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const canReadBilling = hasPermission(user, 'org:billing:read');
  const canReadOrgUsage = hasPermission(user, 'org:usage:read');
  // Same set as the `/keys` nav row. `GET /v1/keys` 403s without it
  // (`routes/v1-keys.ts`), so finance never fires the request — the
  // card says the count is out of reach, matching the balance card's
  // `org:billing:read` empty arm. `org:api_keys:read` still only names
  // the SCOPE of a count that did arrive.
  const canReadKeys = canAccessApiKeys(user);
  const canReadOrgKeys = hasPermission(user, 'org:api_keys:read');
  const balanceQ = useBalance({ enabled: canReadBilling });
  const displayRateQ = useDisplayRate();
  const usageQ = useUsageSummary({ from: firstOfMonthIso(), to: nextMonthStartIso() });
  const keysQ = useKeysList(canReadKeys);
  // One hourly read feeds the stat card, the activity table AND both
  // trend lines. The trend row asks for one day more than it draws —
  // see `TREND_QUERY_DAYS` — because the endpoint's window is rolling
  // hours, not calendar days. The units read is that same window, and
  // only the activity column consults it.
  const hourlyQ = useUsageHourly(TREND_QUERY_DAYS);
  const hourlyUnitsQ = useUsageHourlyUnits(TREND_QUERY_DAYS);
  const statusQ = useNxtpathPublicStatus();
  const byKeyQ = useUsageByKey({ from: firstOfMonthIso(), to: nextMonthStartIso() });

  // Counted straight off the response, with no second client-side
  // filter. `/keys` narrows its LIST to the keys the caller may manage
  // (#8, `fa55f87`), but that is a "what may I administer" cut, not a
  // "how many keys exist" one: an aggregate carries no key name, prefix
  // or owner, so it is not the thing #8 withheld. Re-applying the cut
  // here would also desync this card from the top-keys panel below,
  // which is org-scoped whenever the reader holds `org:usage:read` —
  // the panel would list keys the stat above it did not count.
  const activeKeys = keysQ.data?.keys.filter((k) => k.revokedAt === null).length ?? null;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const balanceUsdMicros =
    balanceQ.data !== undefined
      ? usdMicrosFromCentsFallback(balanceQ.data.balanceUsdMicros, balanceQ.data.balanceUsdCents)
      : null;
  const canManageBilling = hasPermission(user, 'org:billing:manage');

  const lastBucket =
    (hourlyQ.data?.buckets ?? [])
      .filter((b) => b.requests > 0)
      .sort((a, b) => b.hour.localeCompare(a.hour))[0] ?? null;
  const lastRequestAt = lastBucket?.hour ?? null;

  const cards: readonly {
    labelKey: I18nKey;
    value: string;
    hint: JSX.Element;
    testId: string;
  }[] = [
    {
      labelKey: 'dashboard.stat.balance.label',
      value:
        canReadBilling && balanceQ.data !== undefined
          ? // Signed on purpose (#175): an overdraft must read as -¥X, not
            // as held funds. The magnitude-only default is for charge rows.
            formatUsdMicrosOrCentsAsCnyDisplay(
              balanceQ.data.balanceUsdMicros,
              balanceQ.data.balanceUsdCents,
              cnyPerUsd,
              (balanceUsdMicros ?? 0) < 0,
            )
          : '—',
      hint:
        // Without `org:billing:read` the balance query never runs, so
        // the card holds NO balance at all. It may only say that — any
        // sentence about the amount would be invented, and the one this
        // replaced ("balance is empty") was wrong for every org that
        // still has credit.
        !canReadBilling ? (
          <span>{t('dashboard.stat.balance.no_access')}</span>
        ) : balanceQ.data !== undefined ? (
          <span className="flex flex-col gap-0.5">
            {(balanceUsdMicros ?? 0) > 0 ? (
              <>
                <span>
                  {t('dashboard.stat.balance.updated', {
                    timestamp: formatTimestamp(balanceQ.data.updatedAt),
                  })}
                </span>
                {/* Which slice of that number was given, and when it goes away. */}
                <GiftBalanceLine
                  balance={balanceQ.data}
                  cnyPerUsd={cnyPerUsd}
                  testId="dashboard-stat-balance-gift"
                />
              </>
            ) : (balanceUsdMicros ?? 0) < 0 ? (
              // Negative is overdraft, not "empty" (#175): no top-up CTA —
              // a credit customer inside their line owes nothing right now,
              // and the credit line below carries the context.
              <span className="text-bad">{t('dashboard.stat.balance.overdrawn')}</span>
            ) : (
              // Reaching here means the balance was read AND is zero, and
              // every role that can read it can also top up: platform-api
              // hands `org:billing:read` and `org:billing:manage` out
              // together (`permissionsForOrgRole`), so there is no
              // read-only billing role to branch for. The former
              // read-only arm was unreachable, which is exactly how its
              // copy drifted into the no-permission branch above.
              <Link to="/billing/topup" className={HINT_LINK_CLASS}>
                {t('dashboard.stat.balance.zero_cta')}
              </Link>
            )}
            <CreditHeroLine
              balance={balanceQ.data}
              cnyPerUsd={cnyPerUsd}
              testId="dashboard-hero-credit"
            />
          </span>
        ) : balanceQ.isError ? (
          <span>{t('dashboard.stat.balance.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.balance.loading')}</span>
        ),
      testId: 'dashboard-stat-balance',
    },
    {
      labelKey: 'dashboard.stat.cost.label',
      value:
        usageQ.data !== undefined
          ? formatUsdMicrosOrCentsAsCnyDisplay(
              usageQ.data.totalCostUsdMicros,
              usageQ.data.totalCostUsdCents,
              cnyPerUsd,
            )
          : '—',
      // Two captions, because the number means two different things.
      // `/v1/usage` picks its scope from `org:usage:read` alone
      // (platform-api `resolveUsageScope`: with it the whole org,
      // without it `memberAccountId = <caller>`), so the same label
      // over the same figure was answering "mine" for a member and
      // "everyone's" for an admin with no way to tell them apart.
      hint:
        usageQ.data !== undefined ? (
          <span>
            {t(canReadOrgUsage ? 'dashboard.stat.cost.tokens_org' : 'dashboard.stat.cost.tokens', {
              tokens: formatTokens(usageQ.data.totalInputTokens + usageQ.data.totalOutputTokens),
            })}
          </span>
        ) : usageQ.isError ? (
          <span>{t('dashboard.stat.cost.unavailable')}</span>
        ) : (
          <span>{t('dashboard.stat.cost.loading')}</span>
        ),
      testId: 'dashboard-stat-cost',
    },
    {
      labelKey: 'dashboard.stat.keys.label',
      value: activeKeys !== null ? String(activeKeys) : '—',
      // Three captions, because the number means two different things.
      // Zero is scope-free (no org key implies no own key), so it keeps
      // the single "go create one" CTA. Above zero the caption has to
      // name the scope the server picked, otherwise a reader who counts
      // 4 here and finds 1 on `/keys` is told the other 3 are "keys that
      // can still be used right now" — by someone else — with no way to
      // reach them.
      //
      // The org caption is ALWAYS the `/organization` link. There used
      // to be an unlinked fourth arm for a reader holding
      // `org:api_keys:read` but not `org:members:read`; no such reader
      // exists. `permissionsForOrgRole` (platform-api `org-access.ts`)
      // returns one of two fixed sets, and the one carrying
      // `org:api_keys:read` carries `org:members:read` too — the member
      // set carries neither. Keeping copy that can only ever render on
      // an impossible branch is how the wrong sentence reached the
      // reachable one in the first place (cf. #42).
      hint:
        // Without any keys permission the list query never runs, so the
        // card holds NO count at all. It may only say that — any
        // sentence about the number, or a link to `/keys`, would be a
        // dead end for the finance role that lands here.
        !canReadKeys ? (
          <span>{t('dashboard.stat.keys.no_access')}</span>
        ) : keysQ.isError ? (
          <span>{t('dashboard.stat.keys.unavailable')}</span>
        ) : activeKeys === null ? (
          // In flight: the value is still `—`, so there is no number to
          // name a scope for. Naming one here would announce "every key
          // in the organization" above a placeholder.
          <span>{t('dashboard.stat.keys.description')}</span>
        ) : activeKeys === 0 ? (
          <Link to="/keys" className={HINT_LINK_CLASS}>
            {t('dashboard.stat.keys.empty_cta')}
          </Link>
        ) : !canReadOrgKeys ? (
          <span>{t('dashboard.stat.keys.description')}</span>
        ) : (
          <Link
            to="/organization"
            className={HINT_LINK_CLASS}
            data-testid="dashboard-stat-keys-org-link"
          >
            {t('dashboard.stat.keys.description_org_cta')}
          </Link>
        ),
      testId: 'dashboard-stat-keys',
    },
    {
      labelKey: 'dashboard.stat.last_request.label',
      value:
        lastRequestAt !== null
          ? formatTimestamp(lastRequestAt)
          : t('dashboard.stat.last_request.empty'),
      hint:
        lastRequestAt !== null ? (
          <span>{t('dashboard.stat.last_request.hint')}</span>
        ) : canReadKeys ? (
          <Link to="/keys" className={HINT_LINK_CLASS}>
            {t('dashboard.stat.last_request.empty_cta')}
          </Link>
        ) : (
          <span>{t('dashboard.stat.last_request.empty')}</span>
        ),
      testId: 'dashboard-stat-last-request',
    },
  ];

  return (
    <section data-testid="dashboard-page" className="flex flex-col gap-8">
      <PageHeader
        testId="dashboard-header"
        title={t('dashboard.title')}
        // The subtitle advertises what this page can answer. Balance is
        // not one of those things for a reader who cannot read it.
        sub={t(canReadBilling ? 'dashboard.subtitle' : 'dashboard.subtitle_no_billing')}
      />

      <div
        data-testid="dashboard-stat-grid"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        {cards.map((card) => (
          <KpiCard
            key={card.testId}
            testId={card.testId}
            label={t(card.labelKey)}
            value={card.value}
            delta={{ text: card.hint, tone: 'neutral' }}
          />
        ))}
      </div>

      <StatusStrip statusQ={statusQ} />

      <QuickActions canManageBilling={canManageBilling} canAccessApiKeys={canReadKeys} />

      {canReadOrgUsage ? <TrendRow hourlyQ={hourlyQ} cnyPerUsd={cnyPerUsd} /> : null}

      <Panel
        caption={t('dashboard.activity.title')}
        testId="dashboard-activity"
        padded
        actions={
          <Link to="/usage" data-testid="dashboard-activity-link" className={HINT_LINK_CLASS}>
            {t('dashboard.activity.view_all')}
          </Link>
        }
      >
        <DashboardActivity
          buckets={(hourlyQ.data?.buckets ?? []).slice(-10).reverse()}
          unitsByHour={unitsByHourMap(hourlyUnitsQ.data)}
          isLoading={hourlyQ.isLoading}
          isError={hourlyQ.isError}
          onRetry={() => {
            void hourlyQ.refetch();
          }}
          cnyPerUsd={cnyPerUsd}
          canAccessApiKeys={canReadKeys}
        />
      </Panel>

      <Panel
        caption={t('dashboard.top_keys.title')}
        testId="dashboard-top-keys"
        padded
        actions={
          canReadKeys ? (
            <Link to="/keys" data-testid="dashboard-top-keys-link" className={HINT_LINK_CLASS}>
              {t('dashboard.top_keys.manage')}
            </Link>
          ) : undefined
        }
      >
        <DashboardTopKeys
          entries={aggregateTopKeys(byKeyQ.data?.keys ?? []).slice(0, 5)}
          isLoading={byKeyQ.isLoading}
          isError={byKeyQ.isError}
          onRetry={() => {
            void byKeyQ.refetch();
          }}
          cnyPerUsd={cnyPerUsd}
          canAccessApiKeys={canReadKeys}
        />
      </Panel>
    </section>
  );
}

/**
 * Unit quantities aligned on `UsageHourlyBucket.hour`.
 *
 * Only a successful payload is indexed. While that read is in flight,
 * failed, or simply has no row for the hour, the cell stays on tokens:
 * the table's loading and error states belong to the hourly query.
 */
function unitsByHourMap(
  data: UsageHourlyUnitsResponse | undefined,
): ReadonlyMap<string, readonly UsageHourlyUnitQuantity[]> {
  const map = new Map<string, readonly UsageHourlyUnitQuantity[]>();
  for (const bucket of data?.buckets ?? []) {
    map.set(bucket.hour, bucket.units ?? []);
  }
  return map;
}

/**
 * `82,400 Token · 10 秒`. Token-only hours (the units endpoint omits
 * them) keep the token part; a video hour with zero tokens keeps the
 * unit part. Nothing metered at all stays the old `0`.
 */
function activityUsageText(
  inputTokens: number,
  outputTokens: number,
  units: readonly UsageHourlyUnitQuantity[] | undefined,
  t: (key: I18nKey) => string,
): string {
  const parts: string[] = [];
  const tokens = inputTokens + outputTokens;
  if (tokens > 0) {
    parts.push(`${formatTokens(tokens)} ${t('dashboard.activity.unit.tokens')}`);
  }
  const unitText = formatUnitQuantities(units, (unit) => t(`requests.unit_billing.unit.${unit}`));
  if (unitText.length > 0) parts.push(unitText);
  if (parts.length === 0) return formatTokens(0);
  return parts.join(' · ');
}

interface ActivityProps {
  readonly buckets: readonly {
    hour: string;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    costUsdCents: number;
    costUsdMicros?: number;
    costUsd: string;
  }[];
  readonly unitsByHour: ReadonlyMap<string, readonly UsageHourlyUnitQuantity[]>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly cnyPerUsd: number | null;
  readonly canAccessApiKeys: boolean;
}

function DashboardActivity({
  buckets,
  unitsByHour,
  isLoading,
  isError,
  onRetry,
  cnyPerUsd,
  canAccessApiKeys,
}: ActivityProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-activity-loading">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.activity.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <PanelError
        message={t('dashboard.activity.error')}
        retryLabel={t('dashboard.retry')}
        onRetry={onRetry}
        testId="dashboard-activity-error"
      />
    );
  }
  if (buckets.length === 0) {
    return (
      <div className="flex flex-col gap-2 text-body text-ink-low">
        <p>{t('dashboard.activity.empty')}</p>
        {canAccessApiKeys ? (
          <Link to="/keys" className={HINT_LINK_CLASS}>
            {t('dashboard.activity.empty_cta')}
          </Link>
        ) : null}
      </div>
    );
  }
  return (
    <div data-testid="dashboard-activity-table">
      <Table className="border-0 bg-transparent max-md:!bg-transparent">
        <THead>
          <Tr interactive={false}>
            <Th>{t('dashboard.activity.column.hour')}</Th>
            <Th num>{t('dashboard.activity.column.requests')}</Th>
            <Th num>{t('dashboard.activity.column.usage')}</Th>
            <Th num>{t('dashboard.activity.column.cost')}</Th>
          </Tr>
        </THead>
        <TBody>
          {buckets.map((b) => (
            <Tr key={b.hour} data-testid={`dashboard-activity-row-${b.hour}`}>
              <Td mono heading>
                {formatTimestamp(b.hour)}
              </Td>
              <Td num label={t('dashboard.activity.column.requests')}>
                {formatTokens(b.requests)}
              </Td>
              <Td num label={t('dashboard.activity.column.usage')}>
                {activityUsageText(b.inputTokens, b.outputTokens, unitsByHour.get(b.hour), t)}
              </Td>
              <Td num emphasis label={t('dashboard.activity.column.cost')}>
                {formatUsdMicrosOrCentsAsCnyMetered(b.costUsdMicros, b.costUsdCents, cnyPerUsd)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface TopKeyAggregate {
  readonly apiKeyId: string;
  readonly apiKeyName: string | null;
  readonly apiKeyPrefix: string | null;
  readonly requests: number;
  readonly costUsdMicros: number;
}

/**
 * `/v1/usage/by-key` rows are one per key×SKU since epic #898 P6
 * (#913); the "top keys" panel keeps its per-KEY semantics by folding
 * the rows back down to distinct `apiKeyId` before ranking by spend.
 */
function aggregateTopKeys(
  rows: readonly {
    apiKeyId: string;
    apiKeyName: string | null;
    apiKeyPrefix: string | null;
    requests: number;
    costUsdCents: number;
    costUsdMicros?: number;
  }[],
): readonly TopKeyAggregate[] {
  const byKey = new Map<string, TopKeyAggregate>();
  for (const row of rows) {
    const existing = byKey.get(row.apiKeyId);
    byKey.set(row.apiKeyId, {
      apiKeyId: row.apiKeyId,
      apiKeyName: existing?.apiKeyName ?? row.apiKeyName,
      apiKeyPrefix: existing?.apiKeyPrefix ?? row.apiKeyPrefix,
      requests: (existing?.requests ?? 0) + row.requests,
      costUsdMicros:
        (existing?.costUsdMicros ?? 0) +
        usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents),
    });
  }
  return [...byKey.values()].sort((a, b) => b.costUsdMicros - a.costUsdMicros);
}

interface TopKeysProps {
  readonly entries: readonly TopKeyAggregate[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly cnyPerUsd: number | null;
  readonly canAccessApiKeys: boolean;
}

function DashboardTopKeys({
  entries,
  isLoading,
  isError,
  onRetry,
  cnyPerUsd,
  canAccessApiKeys,
}: TopKeysProps): JSX.Element {
  const t = useT();
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="dashboard-top-keys-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-8" />
        ))}
        <span className="sr-only">{t('dashboard.top_keys.loading')}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <PanelError
        message={t('dashboard.top_keys.error')}
        retryLabel={t('dashboard.retry')}
        onRetry={onRetry}
        testId="dashboard-top-keys-error"
      />
    );
  }
  if (entries.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone="neutral">{t('dashboard.top_keys.empty.chip')}</Chip>
        <span className="text-body text-ink-low">{t('dashboard.top_keys.empty.hint')}</span>
        {canAccessApiKeys ? (
          <Link to="/keys" className={HINT_LINK_CLASS}>
            {t('dashboard.top_keys.empty_cta')}
          </Link>
        ) : null}
      </div>
    );
  }
  return (
    <div data-testid="dashboard-top-keys-table">
      <Table className="border-0 bg-transparent max-md:!bg-transparent">
        <THead>
          <Tr interactive={false}>
            <Th>{t('dashboard.top_keys.column.key')}</Th>
            <Th num>{t('dashboard.top_keys.column.requests')}</Th>
            <Th num>{t('dashboard.top_keys.column.cost')}</Th>
          </Tr>
        </THead>
        <TBody>
          {entries.map((k) => (
            <Tr key={k.apiKeyId} data-testid={`dashboard-top-key-${k.apiKeyId}`}>
              <Td emphasis heading mono>
                {apiKeyDisplayName(k.apiKeyName, k.apiKeyPrefix, t)}
              </Td>
              <Td num label={t('dashboard.top_keys.column.requests')}>
                {formatTokens(k.requests)}
              </Td>
              <Td num emphasis label={t('dashboard.top_keys.column.cost')}>
                {formatUsdMicrosOrCentsAsCnyMetered(k.costUsdMicros, 0, cnyPerUsd)}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}
